import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { ORGANIZATION_BY_ID } from '@/graphql/queries/users';
import { clearImpersonation, getImpersonatedOrgId } from '@/utilities/impersonation';

/**
 * The user the app runs as. Only an Onward admin whose simulated org was found gets the org admin overlay;
 * they keep their own user_id and email, so their actions are still attributed to them.
 * @param {{ realUser?: object, simOrgId?: string|null, organization?: object|null, orgMissing?: boolean }} sim
 *   `orgMissing` is true once the org lookup has failed or found nothing
 * @returns {{ user: object, simOrgId: string|null }}
 */
export const resolveOrgSimulation = ({ realUser, simOrgId, organization, orgMissing }) => {
    if (!realUser?.roles?.['ONWARD_ADMIN'] || !simOrgId || orgMissing) return { user: realUser, simOrgId: null };
    return {
        user: { ...realUser, roles: { ADMIN: true }, organization_id: simOrgId, organization: organization || null },
        simOrgId,
    };
};

/**
 * An Onward admin's org admin simulation, re-read from storage when the signed-in identity changes.
 * @returns {{ user: object, simOrgId: string|null, setSimOrgId: Function, loading: boolean }}
 */
export const useOrgSimulation = (realUser, firebaseUid) => {
    const [storedOrgId, setStoredOrgId] = useState(getImpersonatedOrgId);

    // Same re-sync as client_id in App.js: storage is cleared on sign-in and sign-out, and a refresh keeps the sim.
    useEffect(() => {
        const orgId = getImpersonatedOrgId();
        setStoredOrgId(orgId);
    }, [firebaseUid]);

    const simulating = !!realUser?.roles?.['ONWARD_ADMIN'] && !!storedOrgId;

    const { loading, data, error } = useQuery(ORGANIZATION_BY_ID, {
        variables: { organization_id: storedOrgId },
        skip: !simulating,
        onError: (e) => {
            console.error(e);
            Sentry.captureException(e);
        },
    });
    const orgMissing = simulating && !loading && (!!error || (!!data && !data.organization));

    useEffect(() => {
        if (!orgMissing) return;
        clearImpersonation();
        setStoredOrgId(null);
    }, [orgMissing]);

    const resolved = useMemo(
        () => resolveOrgSimulation({ realUser, simOrgId: storedOrgId, organization: data?.organization, orgMissing }),
        [realUser, storedOrgId, data, orgMissing]
    );

    return { ...resolved, setSimOrgId: setStoredOrgId, loading };
};
